import React, { useState } from 'react'
import {
  FieldCommonStylesInterface,
  FormInterface,
} from 'src/shared/types/entities/form-interface'
import {
  useUpdateEntity,
  useUpdatePropNew,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { PaddingGroup } from 'src/editor/shared/settings/groups/padding'
import { BorderGroup } from 'src/editor/shared/settings/groups/border-group'
import { BorderType } from 'common/types/styleTypes'
import { useCalculateFormFieldStyles } from 'src/editor/shared/hooks/use-calculate-form-fields-styles'
import { useThemeColor } from 'src/editor/shared/hooks/use-theme-color'
import { ShadowStyle } from 'client/components/core/Sidebar/components/Settings/styles'
import { Switcher } from 'src/editor/shared/ui-lib/switcher'
import { TypographyGroup } from 'src/editor/shared/settings/groups/typography-group'

enum SettingsModeEnum {
  Style = 'style',
  Typography = 'typography',
}

interface FormFieldsCommonSettingsProps {
  entity: FormInterface
}

export const FormFieldsCommonSettings = ({
  entity,
}: FormFieldsCommonSettingsProps) => {
  const { isMobile } = useDeviceModeContext()

  const [settingsMode, setSettingsMode] = useState<SettingsModeEnum>(
    SettingsModeEnum.Style,
  )

  const updateEntity = useUpdateEntity<FormInterface>()
  const updateProp = useUpdatePropNew(
    entity as FormInterface & {
      fieldsCommonStyles: FieldCommonStylesInterface & {
        border: Partial<BorderType>
        mobileBorder: Partial<BorderType>
      }
    },
  )
  const updateCommonSettings = (
    fieldsCommonStyles: FieldCommonStylesInterface,
  ) => {
    updateEntity({
      ...entity,
      fieldsCommonStyles,
    })
  }
  const commonStyles = entity.fieldsCommonStyles
  const firstUpdateKeys = ['fieldsCommonStyles'] as const

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
  } = useCalculateFormFieldStyles({
    border: commonStyles.border,
    mobileBorder: commonStyles.mobileBorder,
    padding: commonStyles.padding,
    mobilePadding: commonStyles.mobilePadding,
    fontSize: commonStyles.fontSize,
    mobileFontSize: commonStyles.mobileFontSize,
    lineHeight: commonStyles.lineHeight,
    mobileLineHeight: commonStyles.mobileLineHeight,
    textAlign: commonStyles.textAlign,
    mobileTextAlign: commonStyles.mobileTextAlign,
  })

  const { formFields } = useThemeColor()

  const formFieldBackgroundColor = formFields?.formFieldBackgroundColor
  const formFieldTextColor = formFields?.formFieldTextColor
  const formFieldBorderColor = formFields?.formFieldBorderColor

  return (
    <>
      <Switcher
        wrapperClassName="mt-0 mb-8"
        btnList={[
          {
            value: SettingsModeEnum.Style,
            content: 'entity_settings.form.common_settings.style',
          },
          {
            value: SettingsModeEnum.Typography,
            content: 'entity_settings.form.common_settings.typography',
          },
        ]}
        value={settingsMode}
        update={setSettingsMode}
      />

      {settingsMode === SettingsModeEnum.Style && (
        <>
          <PaddingGroup
            padding={
              isMobile ? calculatedPadding.mobile : calculatedPadding.desktop
            }
            update={updateProp(
              ...firstUpdateKeys,
              isMobile ? 'mobilePadding' : 'padding',
            )}
          />

          <BorderGroup
            border={
              isMobile ? calculatedBorder.mobile : calculatedBorder.desktop
            }
            update={updateProp(
              ...firstUpdateKeys,
              isMobile ? 'mobileBorder' : 'border',
            )}
            isMobile={isMobile}
            hideColorControl
          />

          <GroupTitle>website_styles_settings.colors.label</GroupTitle>
          <ColorPickerControl
            isClearable
            label="entity_settings.form.common_settings.border_color"
            color={commonStyles.border?.color || formFieldBorderColor}
            update={updateProp(...firstUpdateKeys, 'border', 'color')}
            mobileColor={commonStyles.mobileBorder?.color}
            mobileUpdate={updateProp(
              ...firstUpdateKeys,
              'mobileBorder',
              'color',
            )}
          />
          <ColorPickerControl
            isClearable
            label="settings_styles.background_color.label"
            color={commonStyles.backgroundColor || formFieldBackgroundColor}
            update={updateProp(...firstUpdateKeys, 'backgroundColor')}
            mobileColor={commonStyles.mobileBackgroundColor}
            mobileUpdate={updateProp(
              ...firstUpdateKeys,
              'mobileBackgroundColor',
            )}
          />
          <ColorPickerControl
            isClearable
            label="text color"
            color={commonStyles.color || formFieldTextColor}
            update={updateProp(...firstUpdateKeys, 'color')}
            mobileColor={commonStyles.mobileColor}
            mobileUpdate={updateProp(...firstUpdateKeys, 'mobileColor')}
          />

          <ShadowStyle
            shadow={commonStyles.boxShadow}
            mobileShadow={commonStyles.mobileBoxShadow}
            update={updateProp(...firstUpdateKeys, 'boxShadow')}
            mobileUpdate={updateProp(...firstUpdateKeys, 'mobileBoxShadow')}
          />
        </>
      )}
      {settingsMode === SettingsModeEnum.Typography && (
        <TypographyGroup
          withoutHorizontalLine
          entity={commonStyles}
          updateEntity={updateCommonSettings}
          hideFontSelector
          hideLetterSpacing
          fontSizeKey="fontSize"
          mobileFontSizeKey="mobileFontSize"
          fontSize={calculatedFontSize.desktop}
          mobileFontSize={calculatedFontSize.mobile}
          updateFontSize={updateProp(...firstUpdateKeys, 'fontSize')}
          updateMobileFontSize={updateProp(
            ...firstUpdateKeys,
            'mobileFontSize',
          )}
          lineHeightKey="lineHeight"
          mobileLineHeightKey="mobileLineHeight"
          lineHeight={calculatedLineHeight.desktop}
          mobileLineHeight={calculatedLineHeight.mobile}
          updateLineHeight={updateProp(...firstUpdateKeys, 'lineHeight')}
          updateMobileLineHeight={updateProp(
            ...firstUpdateKeys,
            'mobileLineHeight',
          )}
          textAlignKey="textAlign"
          mobileTextAlignKey="mobileTextAlign"
          textAlign={calculatedTextAlign.desktop}
          mobileTextAlign={calculatedTextAlign.mobile}
          updateTextAlign={updateProp(...firstUpdateKeys, 'textAlign')}
          updateMobileTextAlign={updateProp(
            ...firstUpdateKeys,
            'mobileTextAlign',
          )}
        />
      )}
    </>
  )
}
